import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { useColorMode } from '@/hooks';
import { MenuItemStyled, MenuStyled } from './MoreVerticalButton.styles';

interface Props {
    color?: string;
    padding?: string;
    disabled?: boolean;
    options: {
        title: string;
        id: number;
        function: (id: number) => void;
        name: string;
    }[];
}

export const MoreVerticalButton = (props: Props) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { t } = useTranslation();
    const isDarkMode = useColorMode();

    const {
        options,
        color = isDarkMode ? theme.palette.primary.main : theme.palette.info.contrastText,
        padding = '8px',
        disabled = false,
    } = props;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const getIcon = (name: string) => {
        switch (name) {
            case 'go-to-pep"':
                return <ClearAllIcon sx={{ marginRight: '12px' }} />;
            case 'go-to-scene-editor"':
                return <ArticleIcon sx={{ marginRight: '12px' }} />;
            case 'delete':
                return <DeleteIcon sx={{ marginRight: '12px' }} />;
            case 'add':
                return <AddIcon sx={{ marginRight: '12px' }} />;
            case 'edit':
                return <EditIcon sx={{ marginRight: '12px' }} />;
            default:
                return undefined;
        }
    };

    return (
        <>
            <IconButton
                sx={{
                    padding,
                }}
                aria-label="menu"
                aria-haspopup="true"
                onClick={handleOpen}
            >
                <MoreVertIcon
                    sx={{
                        color,
                    }}
                />
            </IconButton>
            <MenuStyled
                disableScrollLock
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            backgroundColor: isDarkMode ? theme.palette.secondary.light : theme.custom.c24,
                            backdropFilter: 'blur(30px)',
                        },
                    },
                }}
            >
                {options.map(option => (
                    <MenuItemStyled
                        disabled={disabled}
                        key={option.title}
                        onClick={e => {
                            e.stopPropagation();
                            setAnchorEl(null);
                            option.function(option.id);
                        }}
                        sx={{
                            '&:hover': {
                                color: theme.custom.c4,
                            },
                        }}
                    >
                        {getIcon(option.name)}
                        <Text variant="body1" color="text-inherit" children={t(option.title)} />
                    </MenuItemStyled>
                ))}
            </MenuStyled>
        </>
    );
};
